import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin, of } from 'rxjs';
import { catchError, filter, finalize, map, switchMap } from 'rxjs/operators';
import { SnackNotificationsDialogService } from '@ui-lib/services';
import { ResentOrder, ResentOrdersPagination, RxHubService } from '../../../../services/rx-hub/rx-hub.service';
import { ResendConfirmDialogComponent, ResendConfirmDialogData } from './resend-confirm-dialog/resend-confirm-dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'rx-hub-resent-orders',
  imports: [FormsModule, DatePipe],
  providers: [RxHubService],
  templateUrl: './rx-hub-resent-orders.component.html',
  styleUrl: './rx-hub-resent-orders.component.scss',
})
export class RxHubResentOrdersComponent {
  private destroyRef = inject(DestroyRef);
  private rxHubService = inject(RxHubService);
  private snackBar = inject(SnackNotificationsDialogService);
  private dialog = inject(MatDialog);

  orders = signal<ResentOrder[]>([]);
  isLoading = signal(false);
  httpRequestInProgress = signal(false);

  pagination = signal<ResentOrdersPagination & { total: number }>({
    limit: 20,
    total: 0,
    offset: 0,
    sort: 'dispense_order_id',
    order: 'desc',
    search: '',
  });

  readonly currentPage = computed(() => {
    const { offset, limit } = this.pagination();
    return Math.ceil(offset > 0 ? offset / limit + 1 : 1);
  });

  readonly totalPages = computed(() => {
    const { total, limit } = this.pagination();
    return Math.ceil(total / limit);
  });

  readonly pages = computed(() =>
    Array.from({ length: this.totalPages() }, (_, i) => i + 1),
  );

  readonly hasNextPage = computed(() => {
    const { offset, limit, total } = this.pagination();
    return (offset / limit + 1) * limit < total;
  });

  readonly hasPreviousPage = computed(() => this.currentPage() > 1);

  readonly itemsPerPageOptions: number[] = [10, 20, 50, 75, 100];

  onSearch(): void {
    this.pagination.update(p => ({ ...p, offset: 0 }));
    this.onRefineResentOrders();
  }

  onRefineResentOrders(): void {
    this.isLoading.set(true);
    this.orders.set([]);

    const searchValues = (this.pagination().search ?? '').trim();

    if (!searchValues) {
      this.isLoading.set(false);
      return;
    }

    const originalSearch = this.pagination().search;
    const ids = [...new Set(searchValues.split(/\s+/).filter(Boolean))];

    const requests = ids.map(id =>
      this.rxHubService.get({ ...this.pagination(), search: id }).pipe(
        takeUntilDestroyed(this.destroyRef),
        map(response => response.orders ?? []),
        catchError(() => of([] as ResentOrder[])),
      ),
    );

    forkJoin(requests)
      .pipe(takeUntilDestroyed(this.destroyRef),
      finalize(() => this.isLoading.set(false)))
      .subscribe(results => {
        const merged = results.reduce((acc, orders) => acc.concat(orders), [] as ResentOrder[]);
        this.orders.set(merged);
        this.pagination.update(p => ({ ...p, total: merged.length, search: originalSearch }));
      });
  }

  clearSearch(): void {
    this.pagination.update(p => ({ ...p, search: '' }));
  }

  updateSearch(value: string): void {
    this.pagination.update(p => ({ ...p, search: value }));
  }

  onPageChange(page: number): void {
    this.pagination.update(p => ({ ...p, offset: p.limit * (page - 1) }));
    this.loadOrders();
  }

  goNext(): void {
    this.pagination.update(p => ({ ...p, offset: p.offset + p.limit }));
    this.loadOrders();
  }

  goPrevious(): void {
    this.pagination.update(p => ({ ...p, offset: Math.max(0, p.offset - p.limit) }));
    this.loadOrders();
  }

  onLimitChange(limit: number): void {
    this.pagination.update(p => ({ ...p, limit, offset: 0 }));
    this.loadOrders();
  }

  onSortChange(column: string): void {
    this.pagination.update(p => ({
      ...p,
      sort: column,
      order: p.sort === column && p.order === 'asc' ? 'desc' : 'asc',
      offset: 0,
    }));
    this.loadOrders();
  }

  resendOrder(order: ResentOrder): void {
    const dialogRef = this.dialog.open<ResendConfirmDialogComponent, ResendConfirmDialogData, boolean>(
      ResendConfirmDialogComponent,
      {
        data: {
          dispenseOrderId: order.id,
          orderId: order.order_id,
          externalOrderNumber: order.external_order_number,
        },
        disableClose: true,
      },
    );

    dialogRef.afterClosed().pipe(
      filter(confirmed => confirmed === true),
      switchMap(() => {
        this.httpRequestInProgress.set(true);
        this.isLoading.set(true);
        return this.rxHubService.put(order.id, order.order_id).pipe(
          finalize(() => {
            this.httpRequestInProgress.set(false);
            this.isLoading.set(false);
          }),
        );
      }),
    ).subscribe({
      next: () => {
        this.snackBar.openSuccessMessage('Order resent successfully');
      },
      error: this.handleError,
    });
  }

  private handleError = (error: HttpErrorResponse) =>  {
    const method = error.url ? 'PUT' : '';
    const url = error.url ?? '';
    const status = error.status ?? '';
    const message = error.error?.message ?? error.message ?? '';
      
    this.snackBar.openErrorMessage(
      `Failed to process request: <br><br>${method} -> ${url} <br>${status}: ${message}`,
    );
  };

  loadOrders(): void {
    this.isLoading.set(true);
    this.rxHubService
      .get(this.pagination())
      .pipe(
        takeUntilDestroyed(this.destroyRef), 
        finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: response => {
          const orders = response.orders ?? [];
          this.orders.set(orders);
          this.pagination.update(p => ({ ...p, total: orders.length }));
        },
        error: this.handleError,
      });
  }
}
